import React, { useMemo, useState } from 'react';

const SPORT_COLORS = {
    'nba': '#f97316',
    'afl': '#3b82f6',
    'nfl': '#22c55e',
    'nhl': '#8b5cf6',
    'mlb': '#e11d48',
    'soccer': '#06b6d4',
    'tennis': '#eab308',
    'cricket': '#ec4899',
};

function SportBreakdown({ bets, displayMode = 'dollars', unitSize = 25, getUnitSizeForDate, advancedUnitSettings = false }) {
    const [sortBy, setSortBy] = useState('pl');
    const isUnits = displayMode === 'units';

    const sportData = useMemo(() => {
        const grouped = {};

        bets.forEach(bet => {
            const sport = (bet.sport || 'unknown').toLowerCase();
            const divisor = isUnits && advancedUnitSettings && getUnitSizeForDate ? getUnitSizeForDate(bet.date) : (isUnits ? unitSize : 1);

            if (!grouped[sport]) {
                grouped[sport] = {
                    name: sport,
                    bets: 0,
                    wins: 0,
                    losses: 0,
                    totalStaked: 0,
                    totalReturned: 0,
                    pl: 0
                };
            }

            grouped[sport].bets++;
            if (!bet.bonus_bet) {
                grouped[sport].totalStaked += bet.stake / divisor;
            }
            grouped[sport].pl += bet.pl / divisor;

            if (bet.result !== 'pending') {
                grouped[sport].totalReturned += ((bet.bonus_bet ? 0 : bet.stake) + bet.pl) / divisor;
            }

            if (bet.result === 'win') grouped[sport].wins++;
            if (bet.result === 'loss') grouped[sport].losses++;
        });

        return Object.values(grouped)
            .map(s => ({
                ...s,
                winRate: (s.wins + s.losses) > 0 ? (s.wins / (s.wins + s.losses)) * 100 : 0,
                roi: s.totalStaked > 0 ? (s.pl / s.totalStaked) * 100 : 0
            }))
            .sort((a, b) => {
                if (sortBy === 'pl') return b.pl - a.pl;
                if (sortBy === 'roi') return b.roi - a.roi;
                return 0;
            });
    }, [bets, sortBy, isUnits, unitSize, advancedUnitSettings, getUnitSizeForDate]);

    const chartData = useMemo(() => {
        return [...sportData].sort((a, b) => b.pl - a.pl);
    }, [sportData]);

    const maxAbsPL = Math.max(...sportData.map(s => Math.abs(s.pl)), 1);
    const totalPL = sportData.reduce((sum, s) => sum + s.pl, 0);

    const formatCurrency = (value) => {
        const prefix = value >= 0 ? '+' : '';
        if (isUnits) return `${prefix}${value.toFixed(1)}U`;
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    };

    const formatCurrencyFull = (value) => {
        const prefix = value >= 0 ? '+' : '';
        if (isUnits) return `${prefix}${value.toFixed(1)}U`;
        return `${prefix}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatStaked = (value) => {
        if (isUnits) return `${value.toFixed(1)}U`;
        return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const getSportColor = (name) => SPORT_COLORS[name] || '#64748b';

    if (sportData.length <= 1) return null;

    return (
        <div className="bg-[#0f172a] rounded border border-slate-700/50 overflow-hidden">
            {/* Command Bar Header */}
            <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b border-slate-700/50 bg-slate-800/30">
                <div className="flex items-center gap-2 md:gap-3 min-w-0">
                    <div className={`w-1 h-4 ${totalPL >= 0 ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <h3 className="text-xs md:text-sm font-semibold text-white tracking-tight whitespace-nowrap">Performance by Sport</h3>
                    <span className="hidden md:inline text-[10px] font-mono text-slate-600 tracking-widest uppercase">{sportData.length} sports</span>
                </div>
                <div className={`text-base md:text-lg font-bold font-mono tabular-nums ${totalPL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {formatCurrencyFull(totalPL)}
                </div>
            </div>

            <div className="p-3 md:p-4">
                {/* P/L by Sport - Horizontal Bar Chart */}
                <div className="mb-4">
                    <div className="flex items-center gap-1.5 mb-3">
                        <div className="w-1 h-1 rounded-full bg-emerald-500" />
                        <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">P/L Distribution</div>
                    </div>
                    <div className="space-y-1.5">
                        {chartData.map((sport) => {
                            const barWidth = (Math.abs(sport.pl) / maxAbsPL) * 50;
                            const isPositive = sport.pl >= 0;

                            return (
                                <div key={sport.name} className="flex items-center gap-2 group" title={sport.name.toUpperCase()}>
                                    <div className="w-9 flex items-center justify-center">
                                        <div
                                            className="px-1 py-0.5 rounded text-[9px] font-mono font-bold text-white"
                                            style={{ backgroundColor: getSportColor(sport.name) }}
                                        >
                                            {sport.name.substring(0, 3).toUpperCase()}
                                        </div>
                                    </div>

                                    <div className="flex-1 flex items-center">
                                        <div className="w-1/2 flex justify-end">
                                            {!isPositive && (
                                                <div
                                                    className="h-4 bg-red-500/70 group-hover:bg-red-500 transition-all"
                                                    style={{ width: `${barWidth}%` }}
                                                />
                                            )}
                                        </div>
                                        <div className="w-px h-5 bg-slate-700" />
                                        <div className="w-1/2 flex justify-start">
                                            {isPositive && (
                                                <div
                                                    className="h-4 bg-emerald-500/70 group-hover:bg-emerald-500 transition-all"
                                                    style={{ width: `${barWidth}%` }}
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="w-20 text-right">
                                        <span className={`text-[11px] font-mono font-semibold tabular-nums ${isPositive ? 'text-emerald-400' : 'text-red-400'}`}>
                                            {formatCurrency(sport.pl)}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Performance by Sport - Sortable Table */}
                <div className="border-t border-slate-700/50 pt-3">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5">
                            <div className="w-1 h-1 rounded-full bg-emerald-500" />
                            <div className="text-[10px] font-mono text-slate-500 tracking-widest uppercase">Performance</div>
                        </div>
                        <div className="text-[9px] font-mono text-slate-600 tracking-widest uppercase">
                            Click columns to sort
                        </div>
                    </div>

                    {sportData.length === 0 ? (
                        <div className="text-center py-8 text-[11px] font-mono text-slate-600">
                            No sport data available
                        </div>
                    ) : (
                        <div className="-mx-3 md:-mx-4 overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-800/40 border-y border-slate-700/40">
                                    <tr>
                                        {[
                                            { key: 'name', label: 'Sport', align: 'left', sortable: false, mobile: true },
                                            { key: 'bets', label: 'Bets', align: 'right', sortable: false, mobile: true },
                                            { key: 'record', label: 'W · L', align: 'center', sortable: false, mobile: false },
                                            { key: 'winRate', label: 'Win %', align: 'left', sortable: false, mobile: false },
                                            { key: 'staked', label: 'Staked', align: 'right', sortable: false, mobile: false },
                                            { key: 'returned', label: 'Returned', align: 'right', sortable: false, mobile: false },
                                            { key: 'roi', label: 'ROI', align: 'right', sortable: true, mobile: true },
                                            { key: 'pl', label: 'P/L', align: 'right', sortable: true, mobile: true },
                                        ].map(col => {
                                            const isActive = sortBy === col.key;
                                            const alignCls = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                                            const visibilityCls = col.mobile ? '' : 'hidden md:table-cell';
                                            const baseCls = `px-1.5 md:px-2 py-1.5 text-[9px] font-mono font-semibold tracking-widest uppercase ${alignCls} ${visibilityCls}`;
                                            return col.sortable ? (
                                                <th
                                                    key={col.key}
                                                    onClick={() => setSortBy(col.key)}
                                                    className={`${baseCls} cursor-pointer transition-colors ${
                                                        isActive ? 'text-emerald-400' : 'text-slate-500 hover:text-slate-300'
                                                    }`}
                                                >
                                                    <span className="inline-flex items-center gap-1">
                                                        {col.label}
                                                        {isActive && (
                                                            <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 12 12">
                                                                <path d="M6 8L2 4h8z" />
                                                            </svg>
                                                        )}
                                                    </span>
                                                </th>
                                            ) : (
                                                <th key={col.key} className={`${baseCls} text-slate-500`}>{col.label}</th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/30">
                                    {sportData.map((sport) => {
                                        const winRateClamped = Math.min(Math.max(sport.winRate, 0), 100);
                                        return (
                                            <tr
                                                key={sport.name}
                                                className="hover:bg-slate-800/40 transition-colors"
                                            >
                                                {/* Sport */}
                                                <td className="px-1.5 md:px-2 py-2">
                                                    <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
                                                        <div
                                                            className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold text-white flex-shrink-0"
                                                            style={{ backgroundColor: getSportColor(sport.name) }}
                                                        >
                                                            {sport.name.substring(0, 3).toUpperCase()}
                                                        </div>
                                                        <span className="text-[11px] text-white font-medium truncate">{sport.name.toUpperCase()}</span>
                                                    </div>
                                                </td>
                                                {/* Bets */}
                                                <td className="px-1.5 md:px-2 py-2 text-right text-[11px] font-mono text-slate-300 tabular-nums">
                                                    {sport.bets}
                                                </td>
                                                {/* Record */}
                                                <td className="hidden md:table-cell px-2 py-2 text-center text-[11px] font-mono tabular-nums">
                                                    <span className="text-emerald-400">{sport.wins}</span>
                                                    <span className="text-slate-600 mx-0.5">·</span>
                                                    <span className="text-red-400">{sport.losses}</span>
                                                </td>
                                                {/* Win % bar */}
                                                <td className="hidden md:table-cell px-2 py-2">
                                                    <div className="flex items-center gap-1.5 min-w-[90px]">
                                                        <div className="flex-1 h-1 bg-slate-700/50 rounded-sm overflow-hidden">
                                                            <div
                                                                className={`h-full ${winRateClamped >= 50 ? 'bg-emerald-500/70' : 'bg-amber-500/70'}`}
                                                                style={{ width: `${winRateClamped}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-[10px] font-mono text-slate-300 tabular-nums w-9 text-right">
                                                            {sport.winRate.toFixed(1)}%
                                                        </span>
                                                    </div>
                                                </td>
                                                {/* Staked */}
                                                <td className="hidden md:table-cell px-2 py-2 text-right text-[10px] font-mono text-slate-400 tabular-nums">
                                                    {formatStaked(sport.totalStaked)}
                                                </td>
                                                {/* Returned */}
                                                <td className="hidden md:table-cell px-2 py-2 text-right text-[10px] font-mono text-slate-400 tabular-nums">
                                                    {formatStaked(sport.totalReturned)}
                                                </td>
                                                {/* ROI chip */}
                                                <td className="px-1.5 md:px-2 py-2 text-right">
                                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold tabular-nums ${
                                                        sport.roi >= 0
                                                            ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                                            : 'bg-red-500/10 text-red-400 border border-red-500/20'
                                                    }`}>
                                                        {sport.roi >= 0 ? '+' : ''}{sport.roi.toFixed(1)}%
                                                    </span>
                                                </td>
                                                {/* P/L */}
                                                <td className={`px-1.5 md:px-2 py-2 text-right text-[11px] md:text-[12px] font-mono font-bold tabular-nums whitespace-nowrap ${
                                                    sport.pl >= 0 ? 'text-emerald-400' : 'text-red-400'
                                                }`}>
                                                    {formatCurrencyFull(sport.pl)}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SportBreakdown;
